import React, { useState } from 'react';
import Badge from '../UI/Badge';
import ResumeActions from './ResumeActions';
import DeleteConfirmModal from './DeleteConfirmModal';
import DuplicateModal from './DuplicateModal';
import ShareModal from './ShareModal';
import { timeAgo } from '../../utils/formatters';
import * as resumeService from '../../services/resumeService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export default function ResumeCard({ resume, onEdit, onDelete, onDuplicate, onTogglePublic, view = 'grid', onUpdated }) {
	const [title, setTitle] = useState(resume.title || '');
	const [editing, setEditing] = useState(false);
	const [showDelete, setShowDelete] = useState(false);
	const [showDuplicate, setShowDuplicate] = useState(false);
	const [showShare, setShowShare] = useState(false);
	const navigate = useNavigate();

	async function saveTitle() {
		try {
			await resumeService.updateResume(resume._id, { title });
			toast.success('Title updated');
			onUpdated?.();
		} catch {
			toast.error('Failed to update title');
			setTitle(resume.title || '');
		} finally {
			setEditing(false);
		}
	}

	const ats = resume.atsScore || 0;
	const atsVariant = ats > 75 ? 'success' : ats >= 50 ? 'warning' : 'danger';

	return (
		<div className="border rounded-lg p-4 bg-white hover:shadow-md transition relative">
			<div className="flex items-start justify-between gap-2">
				<div className="flex-1">
					{editing ? (
						<input
							className="text-lg font-semibold border-b w-full focus:outline-none"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							onBlur={saveTitle}
							onKeyDown={(e) => {
								if (e.key === 'Enter') saveTitle();
								if (e.key === 'Escape') {
                                    setEditing(false);
                                    setTitle(resume.title || '');
                                }
							}}
							autoFocus
						/>
					) : (
						<h3 className="text-lg font-semibold cursor-text" onClick={() => setEditing(true)}>
							{title || 'Untitled'}
						</h3>
					)}
					<div className="mt-1 flex items-center gap-2 text-xs text-gray-600">
						<Badge text={resume.templateType} variant="info" />
						<Badge text={`v${resume.version || 1}`} variant="neutral" />
						<Badge text={`ATS ${ats}%`} variant={atsVariant} />
						<span>Updated {timeAgo(resume.updatedAt)}</span>
					</div>
				</div>
				<ResumeActions
					resume={resume}
					onAction={(action) => {
						if (action === 'delete') setShowDelete(true);
						else if (action === 'duplicate') setShowDuplicate(true);
						else if (action === 'share') setShowShare(true);
						else if (action === 'analyze') navigate('/ats-analyzer');
						else if (action === 'match') navigate('/job-matcher');
					}}
				/>
			</div>
			<div className="mt-3 grid grid-cols-4 gap-2">
				<div className="col-span-4 h-28 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-500">Thumbnail</div>
			</div>
			<div className="mt-3 flex items-center justify-between">
				<label className="flex items-center gap-2 text-sm">
					<input type="checkbox" checked={!!resume.isPublic} onChange={async () => { await resumeService.togglePublic(resume._id); onUpdated?.(); }} />
					Public
				</label>
				<div className="flex items-center gap-2">
					<button className="text-sm text-primary-600 underline" onClick={() => navigate(`/resumes/${resume._id}/edit`)}>Edit</button>
					<button className="text-sm text-primary-600 underline" onClick={() => navigate(`/resumes/${resume._id}/preview`)}>Preview</button>
				</div>
			</div>
			<DeleteConfirmModal
				isOpen={showDelete}
				onClose={() => setShowDelete(false)}
				resumeTitle={resume.title}
				onConfirm={async () => {
					setShowDelete(false);
					try {
						await resumeService.deleteResume(resume._id);
						toast.success('Deleted');
						onDelete?.(resume._id);
					} catch {
						toast.error('Delete failed');
					}
				}}
			/>
			<DuplicateModal isOpen={showDuplicate} onClose={() => setShowDuplicate(false)} originalTitle={resume.title} resumeId={resume._id} />
			<ShareModal isOpen={showShare} onClose={() => setShowShare(false)} resume={resume} />
		</div>
	);
}


